"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import Button from "@/components/ui/Button";

const storyParagraphs = [
  "LINDY Balinese Cats began as a quiet promise between two lifelong animal lovers in a peaceful countryside setting. A single encounter with a blue-point Balinese at a cat show revealed a breed whose elegance mirrored its kindness, and the spark to create a dedicated sanctuary was lit.",
  "We knew excellence demanded more than admiration, so we apprenticed with heritage breeders, studied feline genetics at weekend seminars, and invested in mentorships with veterinarians who believed in integrative care. The vision was never to be the biggest—it was to set a standard of warmth, transparency, and artistry.",
  "Every queen and stud who joins our maison is hand-selected for composure, intelligence, and devotion—not simply pedigree. We track lineage health, temperament traits, and the subtle quirks that make a companion feel fated for a specific family.",
  "Today the LINDY atelier is still family-run. Kittens grow up alongside morning coffee rituals, school homework, and evening classical music. No detail is rushed—each pairing, prenatal plan, and placement conversation is paced with reverence for the life we are guiding.",
  "Our guardians often tell us they feel seen and supported. That is the purpose of this work: to send every Balinese into the world radiating confidence because their earliest moments were layered with intention."
];

const guidingPillars = [
  {
    title: "Deliberate Breeding",
    description: "We plan only a handful of litters per year, ensuring every queen receives spacious recovery time, specialized nutrition, and emotional enrichment before and after each pregnancy."
  },
  {
    title: "Whole-Home Socialization",
    description: "From twelve days old, kittens experience gentle handling, aromatherapy, age-appropriate play, and calming household sounds so they arrive ready to bond with busy families."
  },
  {
    title: "Transparent Partnership",
    description: "Prospective guardians receive honest health histories, temperament notes, and transition plans. We remain just a phone call away for every question—day one through forever."
  }
];

const maisonHighlights = [
  {
    heading: "Daily Rhythm",
    text: "Morning mobility checks, mid-day enrichment sessions, and evening cuddle inventory keep kittens confident and people-focused without overstimulation."
  },
  {
    heading: "Health Compass",
    text: "Holistic veterinarians, feline nutritionists, and trusted mentors collaborate on custom vaccination schedules, gut health support, and stress-free travel prep."
  },
  {
    heading: "Guardian Library",
    text: "Every adopter receives a digital library featuring grooming tutorials, menu planners, vet visit checklists, and age-specific enrichment ideas."
  }
];

const teamMembers = [
  {
    role: "Founder & Visionary",
    name: "LINDY Meade",
    blurb: "Curates every pairing and mentors new families with warmth. LINDY believes the Balinese temperament thrives when nurtured in slow, intentional spaces."
  },
  {
    role: "Wellness Steward",
    name: "Dr. Lila Hart, DVM",
    blurb: "Designs preventative care plans and oversees neonatal milestones. Dr. Hart ensures each kitten leaves with a crystal-clear health record."
  },
  {
    role: "Socialization Lead",
    name: "Amari Collins",
    blurb: "Introduces sensory play, travel carriers, and child-friendly interactions so kittens build confidence before they meet their forever families."
  }
];

const communityPromises = [
  "Lifetime concierge support for nutrition, behavior, and wellness questions.",
  "Invitation to seasonal virtual salons featuring grooming demos and Q&A sessions.",
  "Annual alumni check-ins to celebrate milestones and offer tailored advice."
];

const missionStatements = [
  {
    title: "Guardianship First",
    body: "We breed only when our queens are at peak health and calm, ensuring every litter receives unhurried nurture and individualized attention."
  },
  {
    title: "Education Over Sales",
    body: "Families receive transparent health records, temperament notes, and tailored transition plans so adoption feels informed and empowering."
  },
  {
    title: "Legacy of Kindness",
    body: "Our kittens carry the softness they experience. By honoring their emotional needs, we raise companions who remain affectionate through every life stage."
  }
];

const adoptionJourney = [
  {
    title: "Discovery & Dialogue",
    detail: "We connect through detailed consultation to understand your household and preferences"
  },
  {
    title: "Curated Pairing",
    detail: "Personality assessments, pedigree insights, and wellness updates guide us in recommending kittens whose energy and affection style will suit your home."
  },
  {
    title: "Transition Blueprint",
    detail: "You receive a personalized onboarding guide with feeding instructions, scent swapping tips, enrichment ideas, and milestone checklists for the first months."
  },
  {
    title: "Continual Stewardship",
    detail: "Post-placement support through messaging, alumni community, and digital resources"
  }
];

const assurancePoints = [
  "Compliant with all ethical breeding guidelines.",
  "Annual third-party facility wellness audits to validate cleanliness, enrichment, and queen care.",
  "Partner network of feline-specialty veterinarians worldwide for seamless handoffs and ongoing support."
];

export default function AboutPage() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      {/* Hero */}
      <motion.section
        className="px-6 pt-24 pb-16 text-center"
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="mx-auto max-w-4xl space-y-6">
          <span className="inline-block rounded-full bg-white/85 px-5 py-2 text-xs font-semibold uppercase tracking-[0.35em] text-soft-gold">
            About Us
          </span>
          <h1 className="font-heading text-4xl font-bold text-warm-brown sm:text-5xl md:text-6xl">
            Rooted in Care. Elevated by Connection.
          </h1>
          <p className="text-base text-warm-brown/70 sm:text-lg">
            We are a small, intentional cattery cultivating Balinese companions who mirror the tenderness they receive. Our story is woven from late-night bottle feeds, mindful pairing decisions, and the joy of placing each kitten in a home where they are understood.
          </p>
        </div>
      </motion.section>

      <div className="mx-auto max-w-6xl px-6 pb-24 space-y-24">
        {/* Mission Statement */}
        <motion.section
          className="rounded-3xl border border-soft-gold/30 bg-white/90 p-10 shadow-xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.05 }}
        >
          <div className="text-center mb-10 space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Our Mission and Heart</h2>
            <p className="text-warm-brown/70 sm:text-lg">Every Balinese we raise is a promise to nourish trust, beauty, and balance in the homes we serve.</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {missionStatements.map((mission) => (
              <div key={mission.title} className="rounded-2xl bg-cream/55 p-6 shadow-sm space-y-3">
                <h3 className="font-heading text-xl font-semibold text-warm-brown">{mission.title}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed">{mission.body}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Our Story */}
        <motion.section
          className="grid grid-cols-1 gap-12 lg:grid-cols-3"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="lg:col-span-1 space-y-4">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Our Story</h2>
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-soft-gold">From spark to sanctuary</p>
          </div>
          <div className="lg:col-span-2 space-y-5 text-warm-brown/75 leading-relaxed">
            {storyParagraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </motion.section>

        {/* Adoption Journey */}
        <motion.section
          className="rounded-3xl border border-sand/25 bg-white/85 p-10 shadow-lg"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <div className="text-center mb-10 space-y-2">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">What Your Journey Looks Like</h2>
            <p className="text-warm-brown/70 sm:text-lg">We guide every step so you feel confident from first hello to lifelong companionship.</p>
          </div>
          <div className="space-y-6">
            {adoptionJourney.map((phase, index) => (
              <div key={phase.title} className="flex flex-col gap-3 rounded-2xl bg-cream/60 p-6 shadow-sm md:flex-row md:items-start">
                <div className="flex items-center gap-3 md:w-1/5">
                  <span className="rounded-full bg-soft-gold px-4 py-2 text-sm font-semibold text-warm-brown">Step {index + 1}</span>
                </div>
                <div className="md:flex-1 space-y-2">
                  <h3 className="font-heading text-xl font-semibold text-warm-brown">{phase.title}</h3>
                  <p className="text-sm text-warm-brown/70 leading-relaxed">{phase.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Guiding Pillars */}
        <motion.section
          className="rounded-3xl border border-sand/25 bg-white/85 p-10 shadow-lg"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <div className="text-center mb-10 space-y-2">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Guiding Pillars</h2>
            <p className="text-warm-brown/70 sm:text-lg">Every decision is filtered through stewardship, empathy, and transparency.</p>
          </div>
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {guidingPillars.map((pillar) => (
              <div key={pillar.title} className="space-y-3 rounded-2xl bg-cream/60 p-6 shadow-sm">
                <h3 className="font-heading text-xl font-semibold text-warm-brown">{pillar.title}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed">{pillar.description}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Life at the Maison */}
        <motion.section
          className="grid grid-cols-1 gap-10 lg:grid-cols-3"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          {maisonHighlights.map((item) => (
            <div key={item.heading} className="rounded-3xl border border-sand/25 bg-white/80 p-8 shadow">
              <h3 className="font-heading text-xl font-semibold text-warm-brown mb-3">{item.heading}</h3>
              <p className="text-sm text-warm-brown/70 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </motion.section>

        {/* Meet the Team */}
        <motion.section
          className="rounded-3xl border border-soft-gold/25 bg-white/90 p-10 shadow-xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="text-center mb-10 space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">People Behind the Maison</h2>
            <p className="text-warm-brown/70 sm:text-lg">A small, multidisciplinary team keeps love at the center of every routine.</p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {teamMembers.map((member) => (
              <div key={member.name} className="rounded-2xl bg-cream/60 p-6 shadow-sm space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-soft-gold">{member.role}</p>
                <h3 className="font-heading text-lg font-semibold text-warm-brown">{member.name}</h3>
                <p className="text-sm text-warm-brown/70 leading-relaxed">{member.blurb}</p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Community Promise */}
        <motion.section
          className="rounded-3xl bg-gradient-to-r from-soft-gold/15 to-cream/70 p-10 shadow-xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25 }}
        >
          <div className="text-center space-y-4">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Our Promise to the LINDY Community</h2>
            <p className="text-warm-brown/70 sm:text-lg">When you reserve a kitten with us, you join an extended family committed to lifelong flourishing.</p>
          </div>
          <ul className="mt-8 space-y-3 text-sm text-warm-brown/70 leading-relaxed">
            {communityPromises.map((promise) => (
              <li key={promise} className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                <span>{promise}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* Assurance */}
        <motion.section
          className="rounded-3xl border border-sand/25 bg-white/90 p-10 shadow-xl"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.28 }}
        >
          <div className="text-center space-y-3">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Our Ethics & Reputation</h2>
            <p className="text-warm-brown/70 sm:text-lg">We welcome scrutiny because it keeps our promises honest, transparent, and consistent.</p>
          </div>
          <ul className="mt-8 space-y-3 text-sm text-warm-brown/70 leading-relaxed">
            {assurancePoints.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-soft-gold" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </motion.section>

        {/* CTA */}
        <motion.section
          className="text-center"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="mx-auto max-w-3xl space-y-6 rounded-3xl bg-white/90 p-10 shadow-2xl">
            <h2 className="font-heading text-3xl font-bold text-warm-brown">Ready to Create Your Chapter?</h2>
            <p className="text-warm-brown/70">
              Share your story with us and we’ll craft a reservation path tailored to your rhythm, space, and dreams. The sooner you begin, the sooner we can start curating your companion.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row sm:justify-center">
              <Button href="/kittens/application" variant="primary" size="lg" className="w-full sm:w-auto">
                Start Your Application
              </Button>
              <Button href="/contact" variant="outline" size="lg" className="w-full sm:w-auto">
                Connect With Us
              </Button>
            </div>
            <Link href="/process" className="text-sm font-semibold text-soft-gold hover:text-soft-gold/80">
              View our adoption steps →
            </Link>
          </div>
        </motion.section>
      </div>
    </main>
  );
}
